import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Mail, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import EmailConfirmDialog from './EmailConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Send Deal Email Button with dropdown for event types
 * Shows confirmation dialog before sending
 */
const SendDealEmailButton = ({ dealId, getAuthHeaders }) => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);

  const eventTypeLabels = {
    new_deal: 'New Deal',
    funding: 'Funding',
    approval: 'Approval',
    decline: 'Decline',
  };

  const handleEventSelect = async (eventType) => {
    try {
      setLoading(true);
      setSelectedEventType(eventType);

      // Get email preview
      const response = await axios.post(
        `${API}/gmail/preview?event_type=${eventType}&deal_id=${dealId}`,
        {},
        { headers: getAuthHeaders() }
      );

      setEmailPreview(response.data);
      setConfirmOpen(true);
    } catch (error) {
      console.error('Failed to get email preview:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to prepare email';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!emailPreview || !selectedEventType) return;

    try {
      setLoading(true);

      const response = await axios.post(
        `${API}/gmail/send-for-deal?event_type=${selectedEventType}&deal_id=${dealId}`,
        {},
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        toast.success('Email sent successfully!');
      } else {
        toast.error(response.data.error || 'Failed to send email');
      }

      setConfirmOpen(false);
      setEmailPreview(null);
      setSelectedEventType(null);
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={loading}
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            Send Email
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {Object.entries(eventTypeLabels).map(([key, label]) => (
            <DropdownMenuItem
              key={key}
              onClick={() => handleEventSelect(key)}
              disabled={loading}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <EmailConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        emailPreview={emailPreview}
        onConfirm={handleConfirmSend}
        loading={loading}
      />
    </>
  );
};

export default SendDealEmailButton;
